/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionComponent, useMemo } from "react";
import { Editor } from "@tinymce/tinymce-react";

import { invalidPlaceholderStyle } from "@/utils/placeholderValidation";
import { decoratePlaceholderTokens, placeholderPillStyle } from "@/utils/placeholderPills";
import { EMAIL_SAFE_VALID_ELEMENTS } from "@/utils/tinymceEditor.constants";
import { useAppSelector } from "@/redux/hooks";
import { listSelector } from "@/pages/Private/redux/list/list.slice";

import { getSuggestions } from "../../List/utils/column-utils";
import { EventOutreachSchemaType } from "../../List/schema/list";
import { Sender } from "../../List/components/CampaignOverview/components/Sender";
import { getSuggestionLabel } from "../../List/components/CampaignOverview/utils/suggestions.utils";
import { ColumnConfig } from "../../List/schema/columnConfig";

import { normalizeEditorContent } from "./editorContent.util";
import { EditorFooter } from "./EditorFooter/EditorFooter";
import { useEditorFooterWiring } from "./EditorFooter/hooks/useEditorFooterWiring";
import { LINKEDIN_MESSAGE_LIMIT } from "./EditorFooter/utils/editorLimits.constants";
import {
	filterVariableSuggestions,
	VARIABLE_AUTOCOMPLETE_MAX_RESULTS,
} from "./variableAutocomplete.util";

export const LinkedinEditor: FunctionComponent<{
	signature: string;
	handleChange: (value: string) => void;
	handleInputChange?: (value: boolean) => void;
	currentLeadIndex?: number;
	maxLength?: number;
	placeholder?: string;
	sender?: Sender;
	listColumns?: ColumnConfig[];
	listEvent?: EventOutreachSchemaType | null;
	listId?: number;
	onCreateSpintax?: () => void;
	onCreateAiSnippet?: () => void;
	showAiSnippetButton?: boolean;
}> = ({
	signature,
	handleChange,
	handleInputChange,
	currentLeadIndex = 0,
	maxLength,
	placeholder,
	sender,
	listColumns,
	listEvent,
	listId,
	onCreateSpintax,
	onCreateAiSnippet,
	showAiSnippetButton,
}) => {
	const { inbox } = useAppSelector(listSelector);

	const { wireEditorForFooter, charCount, insertToken, captureCursor, getEditor } =
		useEditorFooterWiring();

	const handleFooterTokenInsert = (token: string): boolean => {
		const inserted = insertToken(token);

		if (inserted) {
			handleInputChange?.(true);
			const editor = getEditor();

			if (editor?.getContent) {
				handleChange(normalizeEditorContent(editor.getContent()));
			}
		}

		return inserted;
	};

	const currentPerson = useMemo(() => {
		return inbox[currentLeadIndex];
	}, [currentLeadIndex, inbox]);

	const suggestions = useMemo(() => {
		return inbox[currentLeadIndex]
			? getSuggestions(inbox[currentLeadIndex], true, undefined, listEvent)
			: getSuggestions(undefined, true, listColumns, listEvent);
	}, [currentLeadIndex, inbox, listColumns, listEvent]);

	const processedSuggestions = useMemo(() => {
		return suggestions
			.map((key) => ({
				text: getSuggestionLabel(key, currentPerson, listColumns, listEvent),
				value: key,
			}))
			.filter((item) => item.text !== undefined);
	}, [suggestions, currentPerson, listColumns, listEvent]);

	const pillColumns = useMemo(
		() => listColumns ?? currentPerson?.list?.columns,
		[listColumns, currentPerson]
	);

	return processedSuggestions ? (
		<div className="editor-with-footer relative flex flex-col w-full">
			<Editor
				apiKey={import.meta.env.VITE_TINY_MCE} // optional for cloud features
				init={{
					placeholder: placeholder,
					valid_classes: "mceNonEditable",
					menubar: false,
					height: 360,
					branding: false,
					elementpath: false,
					// The footer bar below the editor owns the char counter; the
					// wordcount plugin stays loaded for getCharacterCount().
					statusbar: false,
					extended_valid_elements:
						"span[data-placeholder|data-invalid|data-pill-type|title|data-mce-noneditable|class]",
					toolbar: "removeformat",

					block_formats: "Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3",
					content_style: `
					body { font-family: Arial, Helvetica, sans-serif; font-size:14px; }

					${placeholderPillStyle}
					${invalidPlaceholderStyle}

          a { text-decoration: underline; }
          table, td { border-collapse: collapse; }

				  `,
					toolbar_mode: "wrap",
					noneditable_noneditable_class: "mceNonEditable",
					plugins: "link lists paste code visualchars wordcount noneditable",
					font_family_formats: `
      Arial=arial,helvetica,sans-serif;
      Georgia=georgia,serif;
	  Helvetica=helvetica,sans-serif;
      Times New Roman='Times New Roman',times,serif;
      Tahoma=tahoma,verdana,sans-serif;
      Trebuchet MS='Trebuchet MS',helvetica,sans-serif;
      Verdana=verdana,geneva,sans-serif;
      Courier New='Courier New',courier,monospace
    `,
					// Keep the HTML simple & email-safe:
					forced_root_block: "p", // avoid <div>
					valid_children: "+body[p]",
					// Only allow common email-safe tags/attrs (no <img> — see constant).
					valid_elements: EMAIL_SAFE_VALID_ELEMENTS,
					// URLs in email should be absolute:
					convert_urls: false,
					relative_urls: false,
					remove_script_host: false,
					paste_as_text: false,
					// Never inline pasted images as base64 (bloats campaignConfig → 413).
					paste_data_images: false,
					// Keep inline styles (fonts/colors) that email clients expect:
					invalid_styles: { "*": "margin" }, // strip margins (often ignored in email)
					uploadcare_public_key: "fcdffd80684e1dd9782f",
					content_css: "/custom-tinymce.css",
					paste_preprocess: function (plugin: any, args: any) {
						// Clean up meta tags
						args.content = args.content.replace(/<meta charset='utf-8'>/g, "");

						// Store the selected text length before it gets replaced
						// @ts-expect-error - tinymce is not defined in the global scope
						const editor = window.tinymce?.activeEditor;

						if (editor) {
							// Get HTML version to properly handle placeholder spans
							let selectedHtml = editor.selection.getContent({ format: "html" }) || "";

							// Convert placeholder spans back to {varName|label} format
							selectedHtml = selectedHtml.replace(
								/<span[^>]*data-placeholder="([^"]+)"[^>]*>[\s\S]*?<\/span>/g,
								(_match: string, varName: string) => `{${varName}}`
							);
							// Now extract text content (which includes {varName|label} syntax)
							const tempDiv = document.createElement("div");

							tempDiv.innerHTML = selectedHtml;
							const selectedText = tempDiv.textContent || "";

							// Store on the editor instance for paste_postprocess to use
							editor._selectedTextLengthBeforePaste = selectedText.length;
						}
					},
					paste_postprocess: function (plugin: any, args: any) {
						// @ts-expect-error - tinymce is not defined in the global scope
						const editor = window.tinymce?.activeEditor;

						if (editor && maxLength) {
							// Get the raw text content from the pasted node
							const rawPastedText = args.node.textContent || "";

							// Normalize placeholder syntax to just the label for consistent counting
							// {varName|label} -> label, {varName} -> varName
							let normalizedPastedText = rawPastedText;

							normalizedPastedText = normalizedPastedText.replace(/\{[^|{}]+\|([^}]+)\}/g, "$1");
							normalizedPastedText = normalizedPastedText.replace(/\{([^}|]+)\}/g, "$1");

							// Calculate the length difference from placeholder syntax
							// This is how many extra raw characters we can allow because placeholders become shorter labels
							const placeholderLengthDiff = rawPastedText.length - normalizedPastedText.length;

							// Calculate current content length (textContent already has labels from rendered spans)
							const bodyOfEditor = editor.getBody();
							const editorContent = bodyOfEditor.textContent || "";

							// editorContent = editorContent.replace(
							// 	/<span[^>]*data-placeholder="([^"]+)"[^>]*>[\s\S]*?<\/span>/g,
							// 	(_match: string, varName: string) => `{${varName}}`
							// );

							// const tempDiv = document.createElement("div");
							// tempDiv.innerHTML = editorContent;
							// const currentText = tempDiv.textContent || "";
							const currentCount = editorContent.trim().length;

							// Account for selected text that will be replaced
							const selectedLength = editor._selectedTextLengthBeforePaste || 0;

							editor._selectedTextLengthBeforePaste = 0; // Reset for next paste

							// Available space for normalized (visible) characters
							let availableVisible = maxLength - currentCount + selectedLength;

							if (availableVisible > maxLength) {
								availableVisible = maxLength;
							}

							// Available space for raw characters (including placeholder syntax)
							const availableRaw = availableVisible;

							if (availableVisible <= 0) {
								// Clear the pasted content entirely
								args.node.textContent = "";
							} else if (normalizedPastedText.length > availableVisible) {
								// Need to truncate - get the inner HTML and work with it
								const walker = document.createTreeWalker(args.node, NodeFilter.SHOW_TEXT, null);

								let charCount = 0;
								let node: Text | null;
								const nodesToProcess: { node: Text; cutAt: number }[] = [];

								// Walk through all text nodes and find where to cut
								// Use availableRaw to account for placeholder syntax that will become shorter
								while ((node = walker.nextNode() as Text | null)) {
									const nodeLength = node.textContent?.length || 0;

									if (charCount + nodeLength > availableRaw) {
										// This node needs to be truncated
										nodesToProcess.push({
											node,
											cutAt: availableRaw - charCount,
										});
										break;
									}

									charCount += nodeLength;
								}

								// Process the truncation
								if (nodesToProcess.length > 0) {
									const { node: textNode, cutAt } = nodesToProcess[0];

									// Truncate this text node
									textNode.textContent = textNode.textContent?.substring(0, cutAt) || "";

									// Remove all siblings after this node and their parents' siblings
									let current: Node | null = textNode;

									while (current && current !== args.node) {
										let sibling = current.nextSibling;

										while (sibling) {
											const next = sibling.nextSibling;

											sibling.parentNode?.removeChild(sibling);
											sibling = next;
										}

										current = current.parentNode;
									}
								}
							}
						}
					},

					setup: function (editor: any) {
						wireEditorForFooter(editor);

						// Disable bold, italic, underline shortcuts
						editor.on("init", () => {
							editor.shortcuts.remove("meta+b");
							editor.shortcuts.remove("meta+i");
							editor.shortcuts.remove("meta+u");
						});

						editor.on("BeforeSetContent", (e: any) => {
							if (!e.content) {
								return;
							}

							// Turn {first_name} into a pill span, **only** if not already wrapped
							e.content = decoratePlaceholderTokens(e.content, {
								processedSuggestions,
								columns: pillColumns,
							});
						});

						// 2) When content is read out of the editor
						editor.on("GetContent", (e: any) => {
							if (!e.content) {
								return;
							}

							// Replace spans back to {variable}
							e.content = e.content.replace(
								/<span[^>]*data-placeholder="([^"]+)"[^>]*>[\s\S]*?<\/span>/g,
								(match: string, varName: string) => `{${varName}}`
							);
						});
						editor.on("keydown", (e: KeyboardEvent) => {
							handleInputChange?.(true);
							const characterCount = editor.plugins.wordcount?.body?.getCharacterCount() ?? 0;

							// Block bold, italic, underline shortcuts
							const isFormattingShortcut =
								(e.metaKey || e.ctrlKey) && ["b", "i", "u"].includes(e.key.toLowerCase());

							if (isFormattingShortcut) {
								e.preventDefault();

								return false;
							}

							const isShortcut =
								(e.metaKey || e.ctrlKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase());

							if (isShortcut) {
								return e;
							}

							if (
								maxLength &&
								characterCount >= maxLength &&
								![
									"Backspace",
									"Delete",
									"ArrowLeft",
									"ArrowRight",
									"ArrowUp",
									"ArrowDown",
								].includes(e.key)
							) {
								e.preventDefault(); // stop further input

								return false;
							}

							return e;
						});

						editor.ui.registry.addAutocompleter("variables", {
							trigger: "{", // ← use this (not `ch`)
							minChars: 0, // show immediately after `{`
							// default predicate requires start-of-word; allow anywhere:
							matches: () => true,
							maxResults: VARIABLE_AUTOCOMPLETE_MAX_RESULTS,
							fetch: (pattern: string) => {
								const list = filterVariableSuggestions(processedSuggestions, pattern, listEvent);

								return Promise.resolve(
									list.map((v) => ({ type: "autocompleteitem", text: v.text, value: v.value }))
								);
							},
							onAction: (api: any, rng: any, value: any) => {
								editor.selection.setRng(rng);

								const text = processedSuggestions.find((v) => v.value === value);
								// If the next char is already '}', avoid duplicating it.
								const node = editor.selection.getNode();
								const contentAfter =
									node?.textContent?.slice(editor.selection.getRng().startOffset) ?? "";
								const needsClose = !contentAfter?.startsWith("}");

								editor.insertContent(
									needsClose ? "{" + value + "|" + (text?.text || value) + "}" : value
								);
								api.hide();
							},
						});

						// Sync content to parent immediately after drop/paste so that
						// consumers reading `selectedNode.data.message` (e.g. AiSnippets'
						// "Test message" button) see the new placeholders without
						// requiring the editor to blur first. The native `input` event
						// fires *after* the content has been inserted, and `inputType`
						// lets us skip normal keystrokes (which stay onBlur-only for
						// performance).
						editor.on("input", (e: InputEvent) => {
							if (e?.inputType === "insertFromDrop" || e?.inputType === "insertFromPaste") {
								handleChange(normalizeEditorContent(editor.getContent()));
							}
						});
					},
				}}
				initialValue={signature}
				onBlur={(e: any) => {
					const text = e.target.getContent();

					handleChange(normalizeEditorContent(text));
				}}
			/>
			<EditorFooter
				captureCursor={captureCursor}
				charCount={charCount}
				charLimit={maxLength ?? LINKEDIN_MESSAGE_LIMIT}
				currentLeadIndex={currentLeadIndex}
				insertToken={handleFooterTokenInsert}
				listColumns={pillColumns}
				listEvent={listEvent}
				listId={listId}
				showAiSnippetButton={showAiSnippetButton}
				onCreateAiSnippet={onCreateAiSnippet}
				onCreateSpintax={onCreateSpintax}
			/>
		</div>
	) : (
		<></>
	);
};
